import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Shield,
  CheckCircle2,
  Clock,
  Ban,
  UserCog,
  Users,
  Loader2,
  LayoutPanelLeft,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LandingEditor from "@/pages/LandingEditor";

const STATUS_META = {
  trial: {
    label: "Trial",
    cls: "bg-amber-100 text-amber-800 border-amber-300",
    icon: Clock,
    next: "active",
    nextLabel: "Activate",
    nextCls: "bg-emerald-600 hover:bg-emerald-700",
  },
  active: {
    label: "Active",
    cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
    icon: CheckCircle2,
    next: "disabled",
    nextLabel: "Disable",
    nextCls: "bg-red-600 hover:bg-red-700",
  },
  disabled: {
    label: "Disabled",
    cls: "bg-red-100 text-red-800 border-red-300",
    icon: Ban,
    next: "active",
    nextLabel: "Re-activate",
    nextCls: "bg-emerald-600 hover:bg-emerald-700",
  },
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function Admin() {
  const { user, booting } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!booting && user?.is_superadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booting, user?.is_superadmin]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(s) ||
        (u.name || "").toLowerCase().includes(s) ||
        (u.status || "").toLowerCase().includes(s),
    );
  }, [users, q]);

  const counts = useMemo(() => {
    const c = { trial: 0, active: 0, disabled: 0 };
    for (const u of users) c[u.status] = (c[u.status] || 0) + 1;
    return c;
  }, [users]);

  if (booting) return <div className="p-8 text-sm text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_superadmin) return <Navigate to="/" replace />;

  const changeStatus = async (u, status) => {
    const label = STATUS_META[status]?.label || status;
    if (!window.confirm(`Set ${u.email} → ${label}?`)) return;
    setSavingId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/status`, { status });
      setUsers((prev) => prev.map((row) => (row.id === u.id ? { ...row, ...data.user } : row)));
      toast.success(`${u.email} → ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
          <Shield size={12} /> Admin
        </div>
        <h1 className="text-4xl font-bold tracking-tight" data-testid="admin-heading">
          Admin Console
        </h1>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="admin-tab-users">
            <Users size={13} className="mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="landing" data-testid="admin-tab-landing">
            <LayoutPanelLeft size={13} className="mr-1.5" /> Landing Page
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <div className="flex items-start justify-between">
            <p className="text-sm text-zinc-500 max-w-xl">
              Switch any user between Trial, Active and Disabled. Disabled users
              cannot sign in. Trial users keep full access but see a banner.
            </p>
            <div className="shrink-0">
              <Input
                placeholder="Search by email, name or status"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-72"
                data-testid="admin-search"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total users" value={users.length} tone="zinc" />
            <StatCard icon={Clock} label="On trial" value={counts.trial || 0} tone="amber" />
            <StatCard icon={CheckCircle2} label="Active" value={counts.active || 0} tone="emerald" />
            <StatCard icon={Ban} label="Disabled" value={counts.disabled || 0} tone="red" />
          </div>

          <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-left">
            <tr>
              <th className="py-3 px-4 font-medium">Name</th>
              <th className="py-3 px-4 font-medium">Email</th>
              <th className="py-3 px-4 font-medium">Signed up</th>
              <th className="py-3 px-4 font-medium text-right">Invoices</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="py-10 text-center text-zinc-400 text-xs">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-10 text-center text-zinc-400 text-xs">
                  No users match your search.
                </td>
              </tr>
            ) : (
              filtered.map((u) => {
                const meta = STATUS_META[u.status] || STATUS_META.trial;
                const Icon = meta.icon;
                return (
                  <tr key={u.id} className="border-t border-zinc-100" data-testid={`admin-row-${u.email}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-sm">{u.name || <span className="text-zinc-400">—</span>}</div>
                      {u.is_superadmin && (
                        <Badge variant="outline" className="mt-1 text-[9px] border-indigo-300 text-indigo-700">
                          <Shield size={9} className="mr-0.5" /> SUPERADMIN
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 mono text-xs text-zinc-600">{u.email}</td>
                    <td className="py-3 px-4 text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                    <td className="py-3 px-4 text-right mono tabular text-xs">{u.invoice_count}</td>
                    <td className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${meta.cls} font-medium text-[10px]`}
                        data-testid={`admin-status-${u.email}`}
                      >
                        <Icon size={10} className="mr-1" /> {meta.label}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {u.is_superadmin ? (
                        <span className="text-[11px] text-zinc-400">—</span>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            className={`h-7 text-[11px] px-2.5 ${meta.nextCls}`}
                            onClick={() => changeStatus(u, meta.next)}
                            disabled={savingId === u.id}
                            data-testid={`admin-toggle-${u.email}`}
                            title={`Set to ${STATUS_META[meta.next]?.label}`}
                          >
                            {savingId === u.id ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : (
                              <>
                                <UserCog size={11} className="mr-1" />
                                {meta.nextLabel}
                              </>
                            )}
                          </Button>
                          {u.status !== "trial" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px] px-2.5"
                              onClick={() => changeStatus(u, "trial")}
                              disabled={savingId === u.id}
                              data-testid={`admin-set-trial-${u.email}`}
                              title="Move back to trial"
                            >
                              Trial
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>
        </TabsContent>

        <TabsContent value="landing">
          <LandingEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "zinc" }) {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-xl font-bold mono tabular">{value}</div>
      </div>
    </Card>
  );
}
